/**
 * New-version detection for the service worker. A deploy installs a new worker that waits behind the running one;
 * without help it only takes over once every tab of the site is closed. This module notices the waiting worker and,
 * when asked, activates it and reloads the page once it controls the page.
 */

let waiting: ServiceWorker | null = null
let listening = false
let reloading = false
const listeners = new Set<() => void>()

function mark(worker: ServiceWorker | null, container: ServiceWorkerContainer) {
  // The very first install has no controller: that is not an update, the page already runs the latest code.
  if (!worker || !container.controller) return
  // A later deploy replaces the worker that was waiting (the browser discards it): always keep the newest.
  const first = waiting === null
  waiting = worker
  if (first) listeners.forEach((listener) => listener())
}

export function isUpdateReady(): boolean {
  return waiting !== null
}

export function onUpdateReady(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function watchRegistration(registration: ServiceWorkerRegistration, container: ServiceWorkerContainer = navigator.serviceWorker): void {
  mark(registration.waiting, container)
  registration.addEventListener('updatefound', () => {
    const worker = registration.installing
    worker?.addEventListener('statechange', () => { if (worker.state === 'installed') mark(worker, container) })
  })
}

export function applyUpdate(container: ServiceWorkerContainer = navigator.serviceWorker, reload: () => void = () => window.location.reload()): void {
  if (!waiting) return
  if (!listening) {
    listening = true
    container.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      reload()
    })
  }
  waiting.postMessage({ type: 'skip-waiting' })
}

/** Routes where a reload would throw away a form being filled in the field. */
export function isFormRoute(pathname: string): boolean {
  return /^\/e\/[^/]+\/(inspect|measure|lubricate)$/.test(pathname) || /^\/inspections\/new(\/|$)/.test(pathname)
}

export function markUpdateReadyForTests(): void {
  waiting = { postMessage: () => undefined } as unknown as ServiceWorker
  listeners.forEach((listener) => listener())
}

export function resetAppUpdate(): void {
  waiting = null
  listening = false
  reloading = false
  listeners.clear()
}
